import { useState, useEffect } from 'react';
import { SearchableGymCard } from './GymCard';
import { Search as SearchIcon } from 'lucide-react';

const FindGymsTab = ({
  userGyms,
  searchQuery,
  onBookOnce,
  onAddGym
}) => {
  const [allGyms, setAllGyms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addingGymId, setAddingGymId] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Load all gyms when search query is entered
  useEffect(() => {
    if (searchQuery.trim() && allGyms.length === 0) {
      loadAllGyms();
    }
    if (searchQuery.trim()) {
      setHasSearched(true);
    }
  }, [searchQuery]);

  const loadAllGyms = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/gyms`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const gyms = await response.json();
        setAllGyms(gyms);
      }
    } catch (error) {
      console.error('Error loading all gyms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddGym = async (gymId) => {
    setAddingGymId(gymId);
    await onAddGym(gymId);
    setAddingGymId(null);
  };

  // Get gyms that match search and aren't already added
  const getSearchResults = () => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    const userGymIds = new Set(userGyms.map(g => g.gymId));

    return allGyms.filter(gym =>
      gym.name.toLowerCase().includes(query) ||
      gym.city?.toLowerCase().includes(query) ||
      gym.state?.toLowerCase().includes(query)
    ).map(gym => ({
      ...gym,
      isAlreadyAdded: userGymIds.has(gym.id)
    }));
  };

  const searchResults = getSearchResults();

  // Empty state (no search entered yet)
  if (!searchQuery.trim() && !hasSearched) {
    return (
      <div className="text-center py-12 px-6">
        <div className="mb-4">
          <SearchIcon className="w-16 h-16 text-slate-300 mx-auto" />
        </div>
        <h3 className="text-lg font-bold text-slate-700 mb-2">
          Find any gym
        </h3>
        <p className="text-sm text-slate-600 mb-6 max-w-sm mx-auto">
          Traveling? Search for any climbing gym to book a quick session or add to your list
        </p>
        <div className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 border-2 border-slate-900 rounded-xl p-4 max-w-sm mx-auto">
          <p className="text-sm text-slate-700">
            <span className="font-bold">Book Once:</span> Reserve without adding to your gym list
          </p>
          <div className="h-px bg-slate-200 my-2"></div>
          <p className="text-sm text-slate-700">
            <span className="font-bold">Add to My Gyms:</span> Save for future visits
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-12 px-4">
        <div className="mb-4">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
        <p className="text-sm text-slate-600">Searching gyms...</p>
      </div>
    );
  }

  // No results
  if (searchQuery.trim() && searchResults.length === 0 && !isLoading) {
    return (
      <div className="text-center py-12 px-4 text-slate-500">
        <p className="mb-2">No gyms found matching "{searchQuery}"</p>
        <p className="text-sm">Try a different search term or location</p>
      </div>
    );
  }

  // Search results
  return (
    <div className="p-4">
      {searchResults.length > 0 && (
        <>
          <div className="mb-4">
            <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide flex items-center gap-2">
              <span className="w-1 h-4 bg-magenta-500 rounded"></span>
              Search Results
            </h3>
            <p className="text-xs text-slate-500 mt-1 ml-3">
              {searchResults.length} {searchResults.length === 1 ? 'gym' : 'gyms'} found
            </p>
          </div>

          <div className="space-y-3">
            {searchResults.map((gym) => (
              <SearchableGymCard
                key={gym.id}
                gym={gym}
                isAlreadyAdded={gym.isAlreadyAdded}
                onBookOnce={() => onBookOnce(gym)}
                onAdd={() => handleAddGym(gym.id)}
                isAdding={addingGymId === gym.id}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FindGymsTab;
